import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

var ResponseCode;
(function (ResponseCode) {
    ResponseCode["OK"] = "OK";
    ResponseCode["NOK"] = "NOK";
})(ResponseCode || (ResponseCode = {}));

/**
 * KBC allowed origins for external parties
 */
const originWhitelist = [
    'https://mobile-f.kbc-group.com',
    'https://mobile-a.kbc-group.com',
    'https://mobile.kbc-group.com',
    'https://mobileyoungsterapp-f.kbc-group.com',
    'https://mobileyoungsterapp-a.kbc-group.com',
    'https://mobileyoungsterapp.kbc-group.com',
    'https://kbctouch-f.kbc.be',
    'https://cbctouch-f.cbc.be',
    'https://touch-f.kbcbrussels.be',
    'https://kbctouch-a.kbc.be',
    'https://cbctouch-a.cbc.be',
    'https://touch-a.kbcbrussels.be',
    'https://kbctouch.kbc.be',
    'https://cbctouch.cbc.be',
    'https://touch.kbcbrussels.be',
];

/**
 * Class used by external third parties to receive events from KBC
 */
class PostmessageExternalReceiver {
    constructor(origins = [], windowInstance = window) {
        this.origins = origins;
        this.windowInstance = windowInstance;
        this.origins = this.origins.concat(originWhitelist);
    }
    parseEventData(data) {
        return Object.prototype.toString.call(data) === '[object String]'
            ? JSON.parse(data)
            : data;
    }
    parsePublicPayload(event) {
        try {
            const parsedEvent = this.parseEventData(event.data);
            if ('api' in parsedEvent && !('token' in parsedEvent) && !('response' in parsedEvent)) {
                return parsedEvent;
            }
            throw new Error('Unknown event type received by PublicPayload');
        }
        catch (e) {
            throw new Error(`Unable to parse event ${event.data}`);
        }
    }
    parsePublicPayloadResponse(event) {
        try {
            const parsedEvent = this.parseEventData(event.data);
            if ('api' in parsedEvent &&
                !('token' in parsedEvent) &&
                'response' in parsedEvent &&
                parsedEvent.api !== 'init') {
                return parsedEvent;
            }
            throw new Error('Unknown event type');
        }
        catch (e) {
            throw new Error(`Unable to parse event ${event.data}`);
        }
    }
    allowOrigin(event) {
        return this.origins.includes(event.origin);
    }
    parseTokenPayload(event) {
        try {
            const parsedEvent = JSON.parse(event.data);
            if ('api' in parsedEvent && 'token' in parsedEvent) {
                return parsedEvent;
            }
            // custom parser for init call
            if (parsedEvent.api === 'init') {
                parsedEvent.token = parsedEvent.response?.authorizationCode;
                delete parsedEvent.response;
                return parsedEvent;
            }
            throw new Error('Unknown event type');
        }
        catch (e) {
            throw new Error(`Unable to parse event ${event.data}`);
        }
    }
    addPublicEventListener(callback) {
        this.publicEventCallback = event => {
            if (this.allowOrigin(event)) {
                try {
                    const { api, data, version, hash } = this.parsePublicPayload(event);
                    callback(api, data, version, hash);
                }
                catch (e) {
                    // not a public event
                }
            }
        };
        this.windowInstance.addEventListener('message', this.publicEventCallback);
        this.publicEventRemoveListener = () => {
            this.windowInstance.removeEventListener('message', this.publicEventCallback);
        };
        return this.publicEventRemoveListener;
    }
    addPublicEventResponseListener(callback) {
        this.publicEventResponseCallback = event => {
            if (this.allowOrigin(event)) {
                try {
                    const { api, response, version } = this.parsePublicPayloadResponse(event);
                    callback(api, response, version);
                }
                catch (e) {
                    // not a public event
                }
            }
        };
        this.windowInstance.addEventListener('message', this.publicEventResponseCallback);
        this.publicEventResponseRemoveListener = () => {
            this.windowInstance.removeEventListener('message', this.publicEventResponseCallback);
        };
        return this.publicEventResponseRemoveListener;
    }
    addTokenEventListener(callback) {
        this.tokenEventCallback = event => {
            if (this.allowOrigin(event)) {
                try {
                    const { api, token, version } = this.parseTokenPayload(event);
                    callback(api, token, version);
                }
                catch (e) {
                    // not a token event
                }
            }
        };
        this.windowInstance.addEventListener('message', this.tokenEventCallback);
        this.tokenEventRemoveListener = () => {
            this.windowInstance.removeEventListener('message', this.tokenEventCallback);
        };
        return this.tokenEventRemoveListener;
    }
}

function filterTokenEvents() {
    return filter((event) => 'api' in event && 'token' in event && !('response' in event));
}
function filterPublicEvents() {
    return filter((event) => 'api' in event && !('token' in event) && !('response' in event));
}
const isPublicEventResponse = (event) => 'api' in event && !('token' in event) && 'response' in event;
function filterPublicEventResponses() {
    return filter(isPublicEventResponse);
}
/**
 * Remap init event to comply with sender update for Olympus
 * This remapping can be removed when 'apiLogonToken' is used instead of 'apiLogonResponse'
 */
function remapInitEventResponses() {
    return map((event) => {
        if (isPublicEventResponse(event) && event.api === 'init') {
            const { api, version } = event;
            return { api, version, token: event.response?.authorizationCode };
        }
        return event;
    });
}
function parsePayload() {
    return map(event => {
        try {
            return JSON.parse(event.data);
        }
        catch (e) {
            throw new Error('Unable to parse event');
        }
    });
}
/**
 * Class used by external third parties to receive events from KBC
 */
class PostmessageExternalReceiverReactive {
    constructor(origins = [], windowInstance = window) {
        this.origins = origins;
        this.windowInstance = windowInstance;
        this.origins = this.origins.concat(originWhitelist);
        this.initObservable();
    }
    filterOrigin() {
        return filter((event) => this.origins.includes(event.origin));
    }
    initObservable() {
        const messageObservable$ = fromEvent(this.windowInstance, 'message').pipe(this.filterOrigin(), parsePayload());
        this.publicEvent$ = messageObservable$.pipe(remapInitEventResponses(), filterPublicEvents());
        this.publicEventResponse$ = messageObservable$.pipe(remapInitEventResponses(), filterPublicEventResponses());
        this.tokenEvent$ = messageObservable$.pipe(remapInitEventResponses(), filterTokenEvents());
    }
}

var name = "@3ps/postmessage-api-external";
var version = "7.0.0";
var scripts = {
	test: "ng test @3ps/postmessage-api-external",
	"build-archive": "yarn --cwd dist pack --filename dist/lma-frontend-tps-postmessage-api.tgz",
	clean: "rm -rf node_modules/ && rm -rf dist",
	"clean-install": "yarn clean && yarn install",
	lint: "tslint -c tslint.json 'src/**/*.ts'",
	"lint-prq": "tslint --force --format checkstyle  -c tslint.json 'src/**/*.ts' --out results/tslint-output.xml",
	"publish-archive": "yarn npm publish dist/lma-frontend-tps-postmessage-api.tgz --new-version $npm_package_version --non-interactive",
	watch: "ng-packagr -w -c ./tsconfig.json"
};
var main = "public_api.ts";
var dependencies = {
	rxjs: "acf:"
};
var devDependencies = {
	"@angular/compiler": "acf:",
	"@angular/compiler-cli": "acf:",
	"@angular/core": "acf:",
	"@types/jasmine": "acf:",
	"jasmine-core": "^5.1.1",
	karma: "^6.4.3",
	"karma-chrome-launcher": "^3.2.0",
	"karma-jasmine": "^5.1.0",
	"ng-packagr": "^21.0.0",
	rxjs: "acf:",
	tslib: "acf:",
	typescript: "acf:",
	"zone.js": "acf:"
};
var pkg = {
	name: name,
	version: version,
	scripts: scripts,
	main: main,
	dependencies: dependencies,
	devDependencies: devDependencies
};

/**
 * Class used by external third parties to send events to KBC
 */
class PostmessageExternalSender {
    constructor(windowInstance = window) {
        this.windowInstance = windowInstance;
        this.version = pkg.version;
        this.regex = 
        // eslint-disable-next-line no-useless-escape
        /^data:([a-z]+\/[a-z0-9-+.]+(;[a-z0-9-.!#$%*+.{}|~`]+=[a-z0-9-.!#$%*+.{}|~`]+)*)?(;base64)?,([a-z0-9!$&',()*+;=\-._~:@\/?%\s]*?)$/i;
    }
    /**
     * @since 201902
     */
    close(force = false) {
        const request = {
            api: 'close',
            data: { force },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202101
     * Use closeWebview for a custom flow
     * For landing on the overview screen use close instead
     */
    closeWebview(viewName) {
        const request = {
            api: 'closeWebview',
            data: { viewName },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201902
     */
    getLocation(options) {
        const request = {
            api: 'getLocation',
            version: this.version,
            data: { options },
        };
        this.sendEvent(request);
    }
    /**
     * @since 201902
     * @param url
     */
    openUrl(url) {
        const request = {
            api: 'openUrl',
            data: { url },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202001
     * @param url
     */
    openUrlEmbedded(url) {
        const request = {
            api: 'openUrlEmbedded',
            data: { url },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201902
     */
    pageLoaded() {
        const request = {
            api: 'pageLoaded',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201902
     */
    pageReady() {
        const request = {
            api: 'pageReady',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201902
     */
    keepAlive() {
        const request = {
            api: 'keepAlive',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201910
     * @param type
     */
    scanBarcode(type) {
        const request = {
            api: 'scanBarcode',
            data: type ? { type } : { type: 'ALL' },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201910
     * @param jwt
     */
    startPaymentToken(jwt) {
        const request = {
            api: 'startPaymentToken',
            token: jwt,
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201910
     * @param cameraOptions contains options for the camera, see cordova camera plugin
     */
    getPicture(cameraOptions) {
        const request = {
            api: 'getPicture',
            data: { cameraOptions },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201910
     * @param token the secure data that needs to be stored at KBC
     */
    storeSecureData(token) {
        const request = {
            api: 'storeSecureData',
            data: { secureData: token },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201910
     * Receive the stored secure data from KBC
     */
    getSecureData() {
        const request = {
            api: 'getSecureData',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @param title: Title of the message
     * @param text: Text shared with the image
     * @param file:
     * mimetype (mediatype meta data: image/png, application/pdf,...)
     * base64; (fixed extension)
     * base64 data (the actual file)
     */
    socialSharing(title, text, file) {
        if (file !== undefined && !this.validDataUrl(file)) {
            return false;
        }
        const request = {
            api: 'socialSharing',
            data: {
                title,
                text,
                file,
            },
            version: this.version,
        };
        this.sendEvent(request);
        return true;
    }
    /**
     * @since 201910
     * @param document: version < 2005: base64 encoded pdf document - version >= 2005: path to
     *     document on BlueCap service
     */
    openPdf(document) {
        const request = {
            api: 'openPdf',
            data: document,
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 201910
     * Show a message on top of the screen (optional customisation for message type and text content)
     */
    showMessage(type, text) {
        const request = {
            api: 'showMessage',
            data: {
                type,
                text,
            },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202002
     * Assign a new url to the parent window.
     * Only available for trusted third party origins where this functionality is specified in the
     * scope.
     * @param destination: url to navigate to.
     */
    navigateTop(destination) {
        const request = {
            api: 'navigateTop',
            data: { destination },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202002
     * Start an authentication flow
     * View readme for more information
     */
    startAuthentication(options) {
        const request = {
            api: 'startAuthentication',
            version: this.version,
            data: options,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202006
     * Make a phone call
     * @param phoneNumber: number to call
     */
    makePhoneCall(phoneNumber) {
        const request = {
            api: 'makePhoneCall',
            data: { phoneNumber },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202006
     * Send an email
     * @param email: recipient email
     */
    sendEmail(email) {
        const request = {
            api: 'sendEmail',
            data: { email },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202101
     * Request a refresh of data
     * @param type: type of the refresh
     */
    refresh(type) {
        const request = {
            api: 'refresh',
            data: { type },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202106
     */
    nextStep(bookingId, providerId, flow) {
        const request = {
            api: 'nextStep',
            data: { bookingId, providerId, flow },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202310
     */
    nextStepV2(providerId, flow, flowParameters) {
        const request = {
            api: 'nextStepV2',
            data: { providerId, flow, flowParameters },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202107
     * Request to receive notifications in the future
     * View readme for more information
     */
    setFutureNotifications(input) {
        if (input?.geolocationIndicator && input.geolocation && !input.geolocation.radius) {
            input.geolocation.radius = 100;
        }
        const request = {
            api: 'manageFutureNotifications',
            data: {
                action: 'SET',
                input,
            },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202107
     * Request to clear future notifications
     * View readme for more information
     */
    clearFutureNotifications(input) {
        const request = {
            api: 'manageFutureNotifications',
            data: {
                action: 'CLEAR',
                input,
            },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202107
     * Request device settings for push messages and locations
     * @param pushMessage: boolean indicating to request device setting for push messages
     * @param location: boolean indicating to request device setting for location
     */
    getDeviceSettingsStatus(getDeviceSettingsInput) {
        const request = {
            api: 'getDeviceSettingsStatus',
            data: getDeviceSettingsInput,
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202107
     * Request to enable device settings for notifications or locations
     * @param enableDeviceSettingsInput: Object containing boolean values for which service to enable
     *     device settings
     * View readme for more info
     */
    enableDeviceSettings(enableDeviceSettingsInput) {
        const request = {
            api: 'enableDeviceSettings',
            data: enableDeviceSettingsInput,
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202112
     * No input is required
     */
    getAccount() {
        const request = {
            api: 'getAccount',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202205
     * No input is required
     */
    cameraStartPreview() {
        const request = {
            api: 'cameraStartPreview',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202205
     * No input is required
     */
    cameraStopPreview() {
        const request = {
            api: 'cameraStopPreview',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202205
     * No input is required
     */
    cameraTakePicture() {
        const request = {
            api: 'cameraTakePicture',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202205
     * No input is required
     */
    cameraPreviewSnapshot() {
        const request = {
            api: 'cameraPreviewSnapshot',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202205
     * No input is required
     */
    switchCameraPreview() {
        const request = {
            api: 'switchCameraPreview',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202305
     * Sends the given page event along with extra details
     */
    sendAnalyticsPageEvent(event, additionalOptions) {
        const request = {
            api: 'analyticsPageEvent',
            data: {
                event,
                ...additionalOptions,
            },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202305
     * Sends the given action event along with extra details
     */
    sendAnalyticsActionEvent(event, additionalOptions) {
        const request = {
            api: 'analyticsActionEvent',
            data: {
                event,
                ...additionalOptions,
            },
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202602
     * Check if the device is eSIM compatible
     * No input is required
     */
    checkESIMCompatibility() {
        const request = {
            api: 'esimCompatibilityCheck',
            version: this.version,
        };
        this.sendEvent(request);
    }
    /**
     * @since 202602
     * Start the installation of an eSIM
     * @param activationCode - the activation code needed to install the eSIM
     */
    startESIMInstallation(activationCode) {
        const request = {
            api: 'esimInstallation',
            data: activationCode,
            version: this.version,
        };
        this.sendEvent(request);
    }
    sendEvent(request) {
        this.windowInstance.parent.postMessage(JSON.stringify(request), '*');
    }
    validDataUrl(dataUrl) {
        const regEx = new RegExp(this.regex);
        return regEx.test(dataUrl);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { PostmessageExternalReceiver, PostmessageExternalReceiverReactive, PostmessageExternalSender, ResponseCode };
//# sourceMappingURL=3ps-postmessage-api-external.mjs.map
